import React, { useState, useEffect } from 'react';
import { Dialog, Box, IconButton, Typography, CircularProgress } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ReactPlayer from 'react-player/lazy';
import { Button } from '../MUIs';
import Global from '../../Services/Common/Global';
import { useSelector } from 'react-redux';

export default function VideoPreview({ open, onClose, videoData, onConfirm }) {
  const [videoUrl, setVideoUrl] = useState('');
  const [videoReady, setVideoReady] = useState(false);
  const [error, setError] = useState(null);
  const { statusBar, navBar } = useSelector((state) => state.systemBar);

  useEffect(() => {
    if (open && videoData) {
      setVideoReady(false);
      setError(null);
      LoadVideo();
    } else {
      setVideoUrl('');
      setVideoReady(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, videoData]);

  const LoadVideo = () => {
    if (
      !videoData?.videoLocation &&
      !videoData?.videoFilePath &&
      !videoData?.videoUrl
    ) {
      setError('동영상 파일 경로가 없습니다.');
      return;
    }

    const filePath =
      videoData.videoLocation ||
      videoData.videoFilePath ||
      videoData.videoUrl;

    const fullUrl = filePath.startsWith('http')
      ? filePath
      : Global.file.useUrl + 'files/' + filePath;

    setVideoUrl(fullUrl);
  };

  const HandleVideoError = (err) => {
    console.error('[tbm] 동영상 로드 에러:', err);
    setError('동영상을 불러올 수 없습니다. 파일이 존재하지 않거나 형식이 지원되지 않을 수 있습니다.');
  };

  const HandleClose = () => {
    setVideoUrl('');
    setVideoReady(false);
    setError(null);
    if (typeof onClose === 'function') onClose();
  };

  const renderContent = () => {
    if (error) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 3,
            height: '100%',
          }}
        >
          <Typography sx={{ color: '#f44336', textAlign: 'center', fontSize: '15px' }}>
            {error}
          </Typography>
          <Button name="닫기" onClick={HandleClose} sx={{ minWidth: 120 }} />
        </Box>
      );
    }

    if (videoUrl) {
      return (
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            bgcolor: '#000000',
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {!videoReady && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                backgroundColor: '#000',
              }}
            >
              <CircularProgress size={50} thickness={6} sx={{ color: '#fff' }} />
              <Typography sx={{ color: '#fff', fontSize: '14px' }}>
                동영상을 불러오는 중...
              </Typography>
            </Box>
          )}
          <ReactPlayer
            url={videoUrl}
            width="100%"
            height="auto"
            controls
            playing={false}
            onReady={() => setVideoReady(true)}
            onError={HandleVideoError}
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                  preload: 'metadata',
                },
              },
            }}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          height: '100%',
        }}
      >
        <CircularProgress size={50} thickness={6} />
        <Typography sx={{ color: '#4b4949ff' }}>동영상을 불러오는 중...</Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={HandleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          m: 0,
          pt: `${statusBar}px`,
          pb: `${navBar}px`,
          borderRadius: 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          backgroundColor: '#fff',
        }}
      >
        {/* 제목 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            gap: 1,
            minWidth: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {videoData?.videoFileName ||
              videoData?.VIDEO_FILE_NAME ||
              videoData?.TITLE ||
              '동영상 미리보기'}
          </Typography>
          <IconButton onClick={HandleClose} sx={{ flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 본문 */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          {renderContent()}
        </Box>

        {/* 확인 버튼 */}
        {!error && onConfirm && (
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Button name="확인" onClick={onConfirm} fullWidth />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
